/**
 * OptimGov password accounts (the alternative to Microsoft sign-in).
 *
 * The server issues a signed session token (12 h) that travels in the same
 * `Authorization: Bearer` header as Microsoft tokens. It is kept in localStorage,
 * like the MSAL cache, so the session survives reloads and new tabs.
 * Server side: server/src/routes/password-auth.routes.ts.
 */

import { API_BASE } from "@/lib/apiUtils"

const STORAGE_KEY = "contractacio.passwordSession"

export interface PasswordSession {
	token: string
	expiresAt: string
	email: string
	name: string
}

/** Mirrors the server policy (password-auth.ts): 10+ characters, 3 of the 4 classes. */
export const PASSWORD_MIN_LENGTH = 10

export function passwordClassCount(pw: string): number {
	return [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((r) => r.test(pw)).length
}

export function meetsPasswordPolicy(pw: string): boolean {
	return pw.length >= PASSWORD_MIN_LENGTH && passwordClassCount(pw) >= 3 && new TextEncoder().encode(pw).length <= 72
}

function isValid(s: unknown): s is PasswordSession {
	const v = s as PasswordSession | null
	return !!v && typeof v.token === "string" && typeof v.expiresAt === "string" && new Date(v.expiresAt).getTime() > Date.now()
}

/** The current session, or null (an expired one is removed). */
export function readPasswordSession(): PasswordSession | null {
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY)
		if (!raw) return null
		const parsed = JSON.parse(raw)
		if (isValid(parsed)) return parsed
		window.localStorage.removeItem(STORAGE_KEY)
	} catch {
		// storage blocked or corrupted: behave as signed out
	}
	return null
}

export function savePasswordSession(s: PasswordSession): void {
	window.localStorage.setItem(STORAGE_KEY, JSON.stringify(s))
}

export function clearPasswordSession(): void {
	try {
		window.localStorage.removeItem(STORAGE_KEY)
	} catch {
		// nothing to clear
	}
}

/** Error code from the server (`invalid_credentials`, `locked`, `invalid_link`, `too_short`...). */
export class PasswordAuthError extends Error {
	constructor(public readonly code: string, public readonly retryAfterSeconds?: number) {
		super(code)
	}
}

async function post<T>(path: string, body: unknown, token?: string): Promise<T> {
	let res: Response
	try {
		res = await fetch(`${API_BASE}/api/auth/password${path}`, {
			method: "POST",
			headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
			body: JSON.stringify(body),
		})
	} catch {
		throw new PasswordAuthError("network")
	}
	const json = (await res.json().catch(() => null)) as { success?: boolean; data?: T; error?: string; retryAfterSeconds?: number } | null
	if (!res.ok || !json?.success) throw new PasswordAuthError(json?.error || "unknown", json?.retryAfterSeconds)
	return json.data as T
}

export async function signInWithPassword(email: string, password: string): Promise<PasswordSession> {
	const s = await post<PasswordSession>("/login", { email, password })
	savePasswordSession(s)
	return s
}

export function checkPasswordLink(token: string): Promise<{ email: string; name: string; hasPassword: boolean }> {
	return post("/setup/check", { token })
}

export async function setPasswordWithLink(token: string, password: string): Promise<PasswordSession> {
	const s = await post<PasswordSession>("/setup", { token, password })
	savePasswordSession(s)
	return s
}

export async function requestPasswordReset(email: string, locale: string): Promise<void> {
	await post("/forgot", { email, locale })
}

export async function changeOwnPasswordAccount(currentPassword: string, newPassword: string): Promise<void> {
	const current = readPasswordSession()
	if (!current) throw new PasswordAuthError("session")
	// The server ends every other session and hands back a fresh one for this tab.
	savePasswordSession(await post<PasswordSession>("/change", { currentPassword, newPassword }, current.token))
}
